import { PapiUser } from '@wix/da-papi-types';
import { createAction } from 'redux-actions';
import { createCallbackActionCreator } from '@wix/da-shared-react/pkg/Stream/helpers';
import { reduxKey } from '../../constants';
import {
  MessageAttachment,
  MessageItem,
  RoomItem,
  RoomType,
} from '../../types';
import { SettableRoomFlagNames } from '../sagas/room/room-management';

export const createRoom = createAction(
  `${reduxKey}.DAMZ_CREATE_ROOM`,
  (roomType: RoomType, userUuids: string[], name: string) => ({
    roomType,
    userUuids,
    name,
  })
);

export const createCommunityChannel = createAction(
  `${reduxKey}.DAMZ_CREATE_COMMUNITY_CHANNEL`,
  (name: string, userUuids: string[]) => ({ name, userUuids })
);

export const selectRoom = createAction(
  `${reduxKey}.DAMZ_SELECT_ROOM`,
  (
    room_id: string,
    messageId: string | undefined,
    forceOpenAnyway: boolean,
    skipDecrement: boolean
  ) => ({
    room_id,
    messageId,
    forceOpenAnyway,
    skipDecrement,
  })
);

export const selectOrCreateRoom = createAction(
  `${reduxKey}.DAMZ_SELECT_OR_CREATE_ROOM`,
  (user: PapiUser, room_id: string | undefined) => ({ user, room_id })
);

export const closeRoom = createAction(
  `${reduxKey}.DAMZ_CLOSE_ROOM`,
  () => ({})
);

export const reloadActiveRoom = createAction(
  `${reduxKey}.DAMZ_RELOAD_ACTIVE_ROOM`,
  () => ({})
);

export const roomChannelReconnected = createAction(
  `${reduxKey}.DAMZ_ROOM_CHANNEL_RECONNECTED`,
  () => ({})
);

export const setActiveRoomData = createAction(
  `${reduxKey}.DAMZ_SET_ROOM_DATA`,
  (roomData: RoomItem) => ({ roomData })
);

export const fetchMessagePrev = createCallbackActionCreator(
  `${reduxKey}.DAMZ_MESSAGE_FETCH_PREV`
);

export const fetchMessageNext = createCallbackActionCreator(
  `${reduxKey}.DAMZ_MESSAGE_FETCH_NEXT`
);

export const presenceJoin = createAction(
  `${reduxKey}.ROOM_PRESENCE_JOIN`,
  (
    room_id,
    joiners
  ): {
    room_id: string;
    joiners: { user_id: string; presence_ref: string }[];
  } => ({
    room_id,
    joiners,
  })
);

export const presenceLeave = createAction(
  `${reduxKey}.ROOM_PRESENCE_LEAVE`,
  (
    room_id,
    leavers
  ): {
    room_id: string;
    leavers: { user_id: string; presence_ref: string }[];
  } => ({
    room_id,
    leavers,
  })
);

export const sendMessage = createAction(
  `${reduxKey}.ROOM_MSG_SEND`,
  isNewRoom => ({ isNewRoom })
);

export const sendMessageFailed = createAction(
  `${reduxKey}.ROOM_MSG_SEND_FAIL`,
  (err, biData) => ({ err, biData })
);

export const clearSendError = createAction(
  `${reduxKey}.ROOM_MSG_CLEAR_SEND_ERROR`,
  () => ({})
);

export const markRoomRead = createAction(
  `${reduxKey}.ROOM_MARK_READ`,
  (room_id: string, skipDecrement: boolean) => ({
    room_id,
    skipDecrement,
  })
);

export const markRoomReadSync = createAction(
  `${reduxKey}.ROOM_MARK_READ_SYNC`,
  () => ({})
);

export const markRoomReadSyncDone = createAction(
  `${reduxKey}.ROOM_MARK_READ_SYNC_DONE`,
  () => ({})
);

/**
 * Will start our process for focusing the user's view on a specific message. Handling differs between messages that are
 * already present in memory and messages we'll have to fetch from the network.
 */
export const requestFocusOnMessage = createAction(
  `${reduxKey}.REQUEST_FOCUS_ON_MESSAGE`,
  (messageId: string, isInitialFetch: boolean) => ({
    messageId,
    isInitialFetch,
  })
);

export const waitForRenderBeforeScrollingToMessage = createAction(
  `${reduxKey}.WAIT_FOR_RENDER_BEFORE_SCROLLING_TO_MESSAGE`,
  () => ({})
);

/**
 * Will let redux know that the message the user wants focused is ready to be scrolled to.
 *
 * IMPORTANT: Only to be used in sagas as part of the {@link requestFocusOnMessage} flow. Will not work on its own.
 */
export const scrollToRequestedMessage = createAction(
  `${reduxKey}.SCROLL_TO_REQUESTED_MESSAGE`,
  (requiredFetch: boolean) => ({ requiredFetch })
);

export const scrollToRequestedMessageDoneScrolling = createAction(
  `${reduxKey}.SCROLL_TO_REQUESTED_MESSAGE_DONE_SCROLLING`,
  () => ({})
);

/**
 * After scrolling to the requested message, we need to drop messages disconnected from the newly
 * loaded chunk and update a few redux values.
 */
export const scrollToRequestedMessageCleanup = createAction(
  `${reduxKey}.SCROLL_TO_REQUESTED_MESSAGE_CLEANUP`,
  () => ({})
);

export const scrollToRequestedMessageCleanupSuccess = createAction(
  `${reduxKey}.SCROLL_TO_REQUESTED_MESSAGE_CLEANUP_SUCCESS`,
  () => ({})
);

export const setUserTyping = createAction(
  `${reduxKey}.ROOM_SET_USER_TYPING`,
  () => ({})
);

export const maybeDisplayTypingIndicator = createAction(
  `${reduxKey}.ROOM_TYPING_MAYBE`,
  state => ({ state })
);

export const stopTyping = createAction(
  `${reduxKey}.ROOM_STOP_TYPING`,
  () => ({})
);

export const setTypingText = createAction(
  `${reduxKey}.ROOM_SET_TYPING_TEXT`,
  text => ({ text })
);

export const stopTypingLater = createAction(
  `${reduxKey}.ROOM_STOP_TYPING_LATER`,
  (delay: number) => ({ delay })
);

export const incrementMessageSent = createAction(
  `${reduxKey}.ROOM_MSG_SENT_INC`,
  () => ({})
);

export const toggleStartNewMessage = createAction(
  `${reduxKey}.ROOM_START_NEW`,
  () => ({})
);

export const closeNewChat = createAction(
  `${reduxKey}.ROOM_CLOSE_NEW_CHAT`,
  () => ({})
);

export const openMessageForUser = createAction(
  `${reduxKey}.ROOM_OPEN_MESSAGE_FOR_USER`,
  (user: PapiUser) => ({ user })
);

export const quoteMessage = createAction(
  `${reduxKey}.QUOTE_MESSAGE`,
  messageId => ({ messageId })
);

export const editMessage = createAction(
  `${reduxKey}.EDIT_MESSAGE`,
  (messageId: string) => ({ messageId })
);

export const setEditMessage = createAction(
  `${reduxKey}.EDIT_MESSAGE_SET`,
  (roomId: string, message: MessageItem) => ({
    roomId,
    message,
  })
);

export const cancelEditMessage = createAction(
  `${reduxKey}.EDIT_MESSAGE_CANCEL`,
  (roomId: string) => ({ roomId })
);

export const editMessageSend = createAction(
  `${reduxKey}.EDIT_MESSAGE_SEND`,
  (
    updatedText: string | undefined,
    updatedAttachments: MessageAttachment[]
  ) => ({ updatedText, updatedAttachments })
);

export const deleteMessage = createAction(
  `${reduxKey}.DELETE_MESSAGE`,
  (messageId: string) => ({ messageId })
);

export const deleteMessageSuccess = createAction(
  `${reduxKey}.DELETE_MESSAGE_SUCCESS`,
  (messageId: string) => ({ messageId })
);

export const deleteMessageFailure = createAction(
  `${reduxKey}.DELETE_MESSAGE_FAILURE`,
  (messageId: string, error: string) => ({ messageId, error })
);

export const markAsRead = createAction(`${reduxKey}.MARK_AS_READ`, room_id => ({
  room_id,
}));

export const markAsUnread = createAction(
  `${reduxKey}.MARK_AS_UNREAD`,
  room_id => ({ room_id })
);

export const updateDrawerRoomLastMsg = createAction(
  `${reduxKey}.DAMZ_UPDATE_DRAWER_ROOM_LAST_MSG`,
  (room_id: string, lastMsg: RoomItem['lastMsg']) => ({ room_id, lastMsg })
);

export const updateDrawerRoomFlagState = createAction(
  `${reduxKey}.DAMZ_UPDATE_DRAWER_ROOM_FLAG_STATE`,
  (
    room_id: string | string[],
    flag: SettableRoomFlagNames,
    value: boolean
  ) => ({
    room_id,
    flag,
    value,
  })
);

export const setMessageSending = createAction(
  `${reduxKey}.SET_MESSAGE_SENDING`,
  (room_id: string, isSending: boolean) => ({ room_id, isSending })
);

export const deleteLeaveRoom = createAction(
  `${reduxKey}.DELETE_LEAVE_ROOM`,
  (room_id: string, isDelete: boolean) => ({ room_id, isDelete })
);

export const deleteLeaveRoomSuccess = createAction(
  `${reduxKey}.DELETE_LEAVE_ROOM_SUCCESS`,
  (room_id: string) => ({ room_id })
);

export const deleteLeaveRoomFailure = createAction(
  `${reduxKey}.DELETE_LEAVE_ROOM_FAILURE`,
  (room_id: string) => ({ room_id })
);

export const toggleStarred = createAction(
  `${reduxKey}.TOGGLE_STARRED`,
  (room_id: string, isStarred: boolean) => ({ room_id, isStarred })
);

export const toggleMuted = createAction(
  `${reduxKey}.TOGGLE_MUTED`,
  (room_id: string, isMuted: boolean) => ({ room_id, isMuted })
);

export const toggleArchived = createAction(
  `${reduxKey}.TOGGLE_ARCHIVED`,
  (room_id: string, isArchived: boolean) => ({ room_id, isArchived })
);

export const unsetRequestFlag = createAction(
  `${reduxKey}.UNSET_REQUEST_FLAG`,
  (room_id: string) => ({ room_id })
);

export const userBlocked = createAction(
  `${reduxKey}.USER_BLOCKED`,
  (room_id: string) => ({ room_id })
);

export const openBlockModal = createAction(
  `${reduxKey}.OPEN_BLOCK_MODAL`,
  (room_id: string) => ({ room_id })
);

export const createGroupChatFromRoom = createAction(
  `${reduxKey}.CREATE_GROUP_CHAT_FROM_ROOM`,
  (room: RoomItem) => ({ room })
);

export const reportMessageSpam = createAction(
  `${reduxKey}.REPORT_MESSAGE_SPAM`,
  (room_id: string, message_id: string) => ({ room_id, message_id })
);

export const renameRoom = createAction(
  `${reduxKey}.RENAME_ROOM`,
  (room_id: string, name: string) => ({ room_id, name })
);

export const renameRoomSuccess = createAction(
  `${reduxKey}.RENAME_ROOM_SUCCESS`,
  (room_id: string, name: string) => ({ room_id, name })
);

export const renameRoomFailure = createAction(
  `${reduxKey}.RENAME_ROOM_FAILURE`,
  (room_id: string, error: string) => ({ room_id, error })
);

/** open the list of multi or community channel participants displayed over
 *  the message window */
export const openRoomParticipantsList = createAction(
  `${reduxKey}.OPEN_ROOM_PARTICIPANTS_LIST`,
  () => ({})
);

/** close the list of multi or community channel participants displayed over
 *  the message window */
export const closeRoomParticipantsList = createAction(
  `${reduxKey}.CLOSE_ROOM_PARTICIPANTS_LIST`,
  () => ({})
);

/** selects a room by room_id (if not active) and opens the list of participants */
export const selectRoomAndOpenParticipantsList = createAction(
  `${reduxKey}.SELECT_ROOM_AND_OPEN_PARTICIPANTS_LIST`,
  (room_id: string) => ({ room_id })
);

/** fetch get_room_participants for the activeRoom */
export const fetchActiveParticipants = createAction(
  `${reduxKey}.FETCH_ROOM_PARTICIPANTS`,
  (room_id: string) => ({ room_id })
);

/** add participants to room.activeDetails */
export const fetchActiveParticipantsSuccess = createAction(
  `${reduxKey}.FETCH_ROOM_PARTICIPANTS_SUCCESS`,
  (participants: PapiUser[]) => ({ participants })
);

/** add papi users to the activeRoom */
export const addParticipants = createAction(
  `${reduxKey}.ADD_PARTICIPANTS`,
  (room_id: string, participants: PapiUser[]) => ({
    room_id,
    participants,
  })
);

/** remove one participant from a room (only works if the user is the owner/admin) */
export const kickParticipant = createAction(
  `${reduxKey}.KICK_PARTICIPANT`,
  (room_id: string, participant: PapiUser) => ({
    room_id,
    participant,
  })
);

export const setRoomUnreadState = createAction(
  `${reduxKey}.SET_ROOM_UNREAD_STATE`,
  (room_id: string, lastViewMessageId: string, unreadMessageCount: number) => ({
    room_id,
    lastViewMessageId,
    unreadMessageCount,
  })
);

export const fetchRoomUnreadState = createAction(
  `${reduxKey}.FETCH_ROOM_UNREAD_STATE`,
  (room_id: string) => ({
    room_id,
  })
);

export const clearRoomJustOpened = createAction(
  `${reduxKey}.CLEAR_ROOM_JUST_OPENED`,
  (room_id: string) => ({
    room_id,
  })
);

export const fetchDmCardData = createAction(
  `${reduxKey}.FETCH_DM_CARD_DATA`,
  () => ({})
);

export const fetchDmCardDataSuccess = createAction(
  `${reduxKey}.FETCH_DM_CARD_DATA_SUCCESS`,
  (watcherCount: number, deviationCount: number, isWatching: boolean) => ({
    watcherCount,
    deviationCount,
    isWatching,
  })
);

export const fetchDmCardDataFailure = createAction(
  `${reduxKey}.FETCH_DM_CARD_DATA_FAILURE`,
  (error: string) => ({ error })
);

export const ignoreRequest = createAction(
  `${reduxKey}.IGNORE_REQUEST`,
  (room_id: string) => ({ room_id })
);

export const undoIgnoreRequest = createAction(
  `${reduxKey}.UNDO_IGNORE_REQUEST`,
  (room_id: string) => ({ room_id })
);

export const acceptRequest = createAction(
  `${reduxKey}.ACCEPT_REQUEST`,
  (room_id: string) => ({ room_id })
);

export const clearActiveRoomItem = createAction(
  `${reduxKey}.CLEAR_ACTIVE_ROOM_ITEM`,
  () => ({})
);
